/**
 * Pure source-of-truth layout synthesizer for sheet cell positioning.
 *
 * Ported verbatim from `tento/tento-lona-js/src/layout-engine.ts` (Phase 2 / 2a).
 * This class has no DOM deps — callers provide a small port
 * interface (column offsets / range widths / span column counts),
 * which lets unit tests assert exact layout outcomes for default,
 * stacked, spanned, and fixed rows.
 *
 * Type surgery from the port: `SheetCell.Layout` (yuzu-ui) becomes
 * `CellLayout` (`./layout-types`); `@tento-lona/row-local-id` becomes
 * `../ids/row-local-id`; `./timescale-utils` (lona-js) becomes
 * `./timescale` (tento/tento-lona-js/sheets).
 */

import type { Option } from "../core/option.ts";
import { PartialDate } from "@tento-chrono";

import type { CellLayout } from "./layout-types.ts";
import type { RowLocalId } from "../ids/row-local-id.ts";
import {
  RowRenderMode,
  Timescale,
} from "./timescale.ts";

/**
 * The DOM-free cell-position synthesizer the sheets-ui binder calls: from a row's render mode and
 * column ports, each cell's `CellLayout`.
 */
export class LayoutEngine {
  readonly #ports: LayoutEngine.Ports;
  constructor(ports: LayoutEngine.Ports) {
    this.#ports = ports;
  }

  resolveCell(input: LayoutEngine.ResolveCellInput): LayoutEngine.Resolution {
    const mode = RowRenderMode.compute(
      input.row.timescale,
      input.sheet.timescale,
      {
        spannable: input.row.spannable,
        stackable: input.row.layout.stackingInfo != null,
      },
      input.sheet.visibleWidthPx,
      input.sheet.resolvedWidthPx,
      null,
    );

    const placement: LayoutEngine.Placement =
      input.row.location === "header" ? "fixed" : "default";

    const anchorColumnOffsetPx = this.#ports.getColumnOffsetPx(
      input.column.offset,
    );
    if (anchorColumnOffsetPx == null) {
      return {
        shape: LayoutEngine.toShape(mode, "covered"),
        placement,
        mode,
        visibility: "virtualized",
        frame: null,
        span: this.#computeSpanMeta(input, mode),
      };
    }

    const subindexFraction = this.#resolveSubindexFraction(input, mode);
    const anchorOffsetPx =
      anchorColumnOffsetPx + input.column.widthPx * subindexFraction;

    if (mode.type === "stacked") {
      const stacking = input.row.layout.stackingInfo;
      if (!stacking) {
        return {
          shape: "default",
          placement,
          mode: { type: "normal" },
          visibility: "rendered",
          frame: {
            anchorColumnOffset: input.column.offset,
            anchorSubindexFraction: subindexFraction,
            anchorOffsetPx,
            xOffsetPx: 0,
            yOffsetPx: 0,
            widthPx: input.column.widthPx,
            heightPx: input.row.layout.cellHeightPx,
          },
          span: null,
        };
      }

      const stackIndex = input.query.stackIndex ?? 0;
      return {
        shape: "stacked",
        placement,
        mode,
        visibility: "rendered",
        frame: {
          anchorColumnOffset: input.column.offset,
          anchorSubindexFraction: subindexFraction,
          anchorOffsetPx,
          xOffsetPx: 0,
          yOffsetPx: stackIndex * stacking.individualCellHeight,
          widthPx: input.column.widthPx,
          heightPx: stacking.individualCellHeight,
        },
        span: null,
      };
    }

    if (mode.type === "spanned") {
      const containingPeriod = Timescale.containingPeriod(
        input.column.period,
        input.row.timescale as Timescale,
      );

      if (mode.isStatic) {
        const spanCount = Timescale.conservativeSpanCount(
          input.row.timescale as Timescale,
          input.sheet.timescale,
        );

        return {
          shape: "static-span",
          placement,
          mode,
          visibility: "hidden",
          frame: {
            anchorColumnOffset: input.column.offset,
            anchorSubindexFraction: subindexFraction,
            anchorOffsetPx,
            xOffsetPx: 0,
            yOffsetPx: 0,
            widthPx: input.column.widthPx,
            heightPx: input.row.layout.cellHeightPx,
          },
          span: {
            period: containingPeriod,
            spanCount,
            spanWidthPx: spanCount * input.sheet.resolvedWidthPx,
            ownerColumnOffset: null,
            ownerOffsetPx: null,
          },
        };
      }

      const ownedPeriod = Timescale.ownedRowPeriod(
        input.column.period,
        input.row.timescale as Timescale,
      );

      if (!ownedPeriod) {
        return {
          shape: "span-hidden",
          placement,
          mode,
          visibility: "hidden",
          frame: {
            anchorColumnOffset: input.column.offset,
            anchorSubindexFraction: subindexFraction,
            anchorOffsetPx,
            xOffsetPx: 0,
            yOffsetPx: 0,
            widthPx: 0,
            heightPx: input.row.layout.cellHeightPx,
          },
          span: {
            period: containingPeriod,
            spanCount: null,
            spanWidthPx: null,
            ownerColumnOffset: null,
            ownerOffsetPx: null,
          },
        };
      }

      const spanCount = this.#ports.getSpanColumnCount(ownedPeriod);
      const ownerColumnOffset = input.column.offset;
      const startOffset = ownerColumnOffset - (spanCount - 1);
      const spanWidthPx = this.#ports.getColumnRangeWidthPx(
        startOffset,
        ownerColumnOffset,
      );
      const ownerOffsetPx = this.#ports.getColumnOffsetPx(ownerColumnOffset);

      return {
        shape: "span-owner",
        placement,
        mode,
        visibility: "rendered",
        frame: {
          anchorColumnOffset: input.column.offset,
          anchorSubindexFraction: subindexFraction,
          anchorOffsetPx,
          xOffsetPx: input.column.widthPx - spanWidthPx,
          yOffsetPx: 0,
          widthPx: spanWidthPx,
          heightPx: input.row.layout.cellHeightPx,
        },
        span: {
          period: ownedPeriod,
          spanCount,
          spanWidthPx,
          ownerColumnOffset,
          ownerOffsetPx,
        },
      };
    }

    return {
      shape: "default",
      placement,
      mode,
      visibility: "rendered",
      frame: {
        anchorColumnOffset: input.column.offset,
        anchorSubindexFraction: subindexFraction,
        anchorOffsetPx,
        xOffsetPx: 0,
        yOffsetPx: 0,
        widthPx: input.column.widthPx,
        heightPx: input.row.layout.cellHeightPx,
      },
      span: null,
    };
  }

  #resolveSubindexFraction(
    input: LayoutEngine.ResolveCellInput,
    mode: RowRenderMode,
  ): number {
    if (input.query.subindexFraction != null) {
      return input.query.subindexFraction;
    }

    if (mode.type !== "stacked") {
      return 0;
    }

    const stacking = input.row.layout.stackingInfo;
    if (!stacking || stacking.cellsPerColumn <= 0) {
      return 0;
    }

    const stackIndex = input.query.stackIndex ?? 0;
    return stackIndex / stacking.cellsPerColumn;
  }

  #computeSpanMeta(
    input: LayoutEngine.ResolveCellInput,
    mode: RowRenderMode,
  ): Option<LayoutEngine.Resolution["span"]> {
    if (mode.type !== "spanned") return null;

    const period = Timescale.containingPeriod(
      input.column.period,
      input.row.timescale as Timescale,
    );

    if (mode.isStatic) {
      const spanCount = Timescale.conservativeSpanCount(
        input.row.timescale as Timescale,
        input.sheet.timescale,
      );
      return {
        period,
        spanCount,
        spanWidthPx: spanCount * input.sheet.resolvedWidthPx,
        ownerColumnOffset: null,
        ownerOffsetPx: null,
      };
    }

    return {
      period,
      spanCount: null,
      spanWidthPx: null,
      ownerColumnOffset: null,
      ownerOffsetPx: null,
    };
  }
}

/**
 * Types and values that go with `LayoutEngine`: `Shape`, `Placement`,
 * `Visibility`, `Row`, `Column`, `Sheet`, and 7 more.
 */
export namespace LayoutEngine {
  export type Shape =
    | "default"
    | "stacked"
    | "span-owner"
    | "span-hidden"
    | "static-span";

  export type Placement = "default" | "fixed";

  export type Visibility = "rendered" | "hidden" | "virtualized";

  export type Row = {
    id: RowLocalId;
    timescale: Timescale | "custom";
    spannable: boolean;
    location: "header" | "body";
    layout: CellLayout;
  };

  export type Column = {
    offset: number;
    widthPx: number;
    period: PartialDate;
  };

  export type Sheet = {
    timescale: Timescale;
    visibleWidthPx: number;
    resolvedWidthPx: number;
  };

  export type Query = {
    stackIndex?: number;
    subindexFraction?: number;
  };

  export type ResolveCellInput = {
    row: Row;
    column: Column;
    sheet: Sheet;
    query: Query;
  };

  export type Frame = {
    anchorColumnOffset: number;
    anchorSubindexFraction: number;
    anchorOffsetPx: number;
    xOffsetPx: number;
    yOffsetPx: number;
    widthPx: number;
    heightPx: number;
  };

  export type SpanMeta = {
    period: PartialDate;
    spanCount: Option<number>;
    spanWidthPx: Option<number>;
    ownerColumnOffset: Option<number>;
    ownerOffsetPx: Option<number>;
  };

  export type Resolution = {
    shape: Shape;
    placement: Placement;
    mode: RowRenderMode;
    visibility: Visibility;
    frame: Option<Frame>;
    span: Option<SpanMeta>;
  };

  export interface Ports {
    getColumnOffsetPx(index: number): Option<number>;
    getColumnRangeWidthPx(start: number, end: number): number;
    getSpanColumnCount(period: PartialDate): number;
  }

  /**
   * The shape a row renders in, in a column that owns its span (`"owner"`)
   * or one the span covers from an earlier column (`"covered"`).
   */
  export function toShape(
    mode: RowRenderMode,
    spanColumn: "owner" | "covered",
  ): LayoutEngine.Shape {
    if (mode.type === "stacked") return "stacked";
    if (mode.type === "spanned" && mode.isStatic) return "static-span";
    if (mode.type === "spanned") return spanColumn === "owner" ? "span-owner" : "span-hidden";
    return "default";
  }
}
